import type { ReactNode } from 'react';
import { NavLink, useLocation, useSearchParams } from 'react-router-dom';
import {
  Bookmark,
  Briefcase,
  FileText,
  Grid2X2,
  LogOut,
  MessageSquare,
  Phone,
  Send,
  Settings,
  ShieldCheck,
  ShoppingBag,
  Sparkles,
  ThermometerSun,
  Trash2,
  Waves,
  Wrench,
  X,
  Calendar,
  Users,
  TrendingUp,
  FileCheck,
  Wallet,
  BarChart3,
  FolderOpen,
  Lock
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import projectService from '../api/projectService';
import type { ProjectTree } from '../pages/ProjectManagement/types';
import type { FacilitySubmodule, PortalRole } from '../types/auth';
import logoApp from '../assets/images/logo.png';
import indoteknisiMan from '../assets/images/indoteknisiMan-removebg.png';

type SidebarProps = {
  onClose?: () => void;
  permissions?: string[];
  userRole: PortalRole;
  facilitySubmodules?: FacilitySubmodule[];
};

type MenuItem = {
  name: string;
  path: string;
  icon: ReactNode;
  badge?: string;
  exact?: boolean;
};

const facilityRoutePaths = ['/cleaning', '/ac', '/waste', '/procurement', '/laundry'];

const getFacilityPath = (submodule: FacilitySubmodule) => {
  const normalizedName = `${submodule.key} ${submodule.value}`.toLowerCase();

  if (normalizedName.includes('ac')) return '/ac';
  if (normalizedName.includes('waste')) return '/waste';
  if (normalizedName.includes('procurement') || normalizedName.includes('pengadaan')) return '/procurement';
  if (normalizedName.includes('laundry')) return '/laundry';
  if (normalizedName.includes('cleaning')) return '/cleaning';

  return `/cleaning?category_name=${encodeURIComponent(submodule.value)}&category_id=${submodule.key.replace('fm_', '')}`;
};

const getIcon = (name: string) => {
  const lowerName = name.toLowerCase();

  if (lowerName.includes('ac')) return <ThermometerSun size={19} />;
  if (lowerName.includes('cleaning')) return <Wrench size={19} />;
  if (lowerName.includes('waste')) return <Trash2 size={19} />;
  if (lowerName.includes('procurement') || lowerName.includes('pengadaan')) return <ShoppingBag size={19} />;
  if (lowerName.includes('laundry')) return <Waves size={19} />;

  return <Sparkles size={19} />;
};

const navItemClassName = (isActive: boolean) =>
  [
    'flex items-center gap-3 rounded-xl px-3.5 py-3 text-sm font-semibold transition-all duration-200',
    isActive
      ? 'bg-blue-50 text-blue-700'
      : 'text-slate-700 hover:bg-blue-50/60 hover:text-blue-700',
  ].join(' ');

const iconClassName = (isActive: boolean) =>
  isActive ? 'text-blue-600' : 'text-slate-500 group-hover:text-blue-600';

const Sidebar = ({
  onClose,
  permissions = [],
  userRole,
  facilitySubmodules = [],
}: SidebarProps) => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  
  const projectId = searchParams.get('project_id') || searchParams.get('projectId');
  const activeTab = searchParams.get('tab') || 'planning';
  const isProjectActiveURL = location.pathname.includes('/project-management') && projectId;

  const parsedProjectId = projectId ? (isNaN(Number(projectId)) ? projectId : Number(projectId)) : null;

  const { data: projectTree } = useQuery({
    queryKey: ["project-management", "project-tree", parsedProjectId ?? "none"],
    queryFn: async () =>
      (await projectService.getProjectTree(parsedProjectId!)) as ProjectTree | null,
    enabled: !!parsedProjectId,
    staleTime: 60_000,
  });

  const isProjectActive = projectTree
    ? ['active', 'in_progress', 'execution', 'converted_to_manpro', 'completed'].includes(String(projectTree.status || '').toLowerCase())
    : true;

  const baseMenus: MenuItem[] = [
    { name: 'Dashboard', path: '/', icon: <Grid2X2 size={19} />, exact: true },
    { name: 'Manajemen Proyek', path: '/project-management', icon: <Briefcase size={19} /> },
  ];

  if (userRole === 'provider') {
    baseMenus.push(
      { name: 'Peluang Provider', path: '/provider-opportunities', icon: <Send size={19} />, badge: 'New' },
      { name: 'Proyek Tersimpan', path: '/provider-favorites', icon: <Bookmark size={19} /> },
      { name: 'Penawaran Saya', path: '/provider-bids', icon: <FileText size={19} /> },
      // { name: 'Kelola Tim', path: '/teknisi', icon: <Users size={19} /> },
    );
  }

  const dynamicMenus: MenuItem[] = Array.from(
    new Map(
      facilitySubmodules.map((submodule) => {
        const path = getFacilityPath(submodule);

        return [
          path,
          {
            name: submodule.value,
            path,
            icon: getIcon(submodule.value),
          },
        ] as const;
      }),
    ).values(),
  );

  const allMenus: MenuItem[] = [
    ...baseMenus,
    ...dynamicMenus,
  ];

  const filteredMenus = allMenus;

  const mainMenus = filteredMenus.filter(
    (menu) =>
      !facilityRoutePaths.some((path) => menu.path.split('?')[0] === path)
  );
  const facilityMenus = filteredMenus.filter((menu) =>
    facilityRoutePaths.some((path) => menu.path.split('?')[0] === path),
  );

  return (
    <aside className="flex h-[125vh] min-h-[125vh] w-[280px] shrink-0 flex-col border-r border-slate-200 bg-white">
      <div className="flex items-start justify-between px-5 pb-6 pt-6">
        <div className="flex items-center gap-3">
          <img src={logoApp} alt="Indoteknisi" className="h-12 w-12 object-contain" />
          <div>
            <p className="text-2xl font-extrabold leading-none text-blue-700">Indoteknisi</p>
            <p className="mt-1 text-xs font-medium text-slate-500">Facility & Manpro</p>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded-xl p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 lg:hidden"
          aria-label="Tutup sidebar"
        >
          <X size={18} />
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto px-4 pb-4">
        <div className="space-y-1.5">
          {mainMenus.map((menu) => (
            <NavLink key={menu.path} to={menu.path} end={menu.exact} onClick={onClose}>
              {({ isActive }) => (
                <div className={`group ${navItemClassName(isActive)}`}>
                  <span className={iconClassName(isActive)}>{menu.icon}</span>
                  <span className="flex-1">{menu.name}</span>
                  {menu.badge && (
                    <span className="rounded-full bg-blue-600 px-2 py-0.5 text-xs font-extrabold text-white">
                      {menu.badge}
                    </span>
                  )}
                </div>
              )}
            </NavLink>
          ))}
        </div>

        {isProjectActiveURL && (
          <div className="mt-6 border-t border-slate-200 pt-5">
            <p className="px-3 pb-2 text-[10px] font-extrabold uppercase tracking-wider text-slate-400">
              PROYEK AKTIF
            </p>
            <div className="flex items-center gap-2 px-3 pb-3">
              <FolderOpen size={16} className="text-blue-600" />
              <p className="text-sm font-extrabold text-slate-800 line-clamp-1">
                Detail Proyek
              </p>
            </div>
            <div className="space-y-1 pl-4 border-l-2 border-slate-100 ml-4">
              {[
                { id: 'planning', label: 'Perencanaan (RAB)', icon: <Calendar size={16} /> },
                { id: 'team', label: 'Kelola Tim', icon: <Users size={16} /> },
                { id: 'execution', label: 'Pelaksanaan Lapangan', icon: <TrendingUp size={16} />, requiresActive: true },
                { id: 'validation', label: 'Pemeriksaan / Validasi', icon: <FileCheck size={16} />, requiresActive: true },
                { id: 'finance', label: 'Keuangan (Termijn)', icon: <Wallet size={16} />, requiresActive: true },
                { id: 'reporting', label: 'Laporan Final', icon: <BarChart3 size={16} />, requiresActive: true },
              ].map((tab) => {
                const isActive = activeTab === tab.id;
                const isLocked = Boolean(tab.requiresActive) && !isProjectActive;

                return (
                  <NavLink
                    key={tab.id}
                    to={isLocked ? '#' : `/project-management?project_id=${projectId}&tab=${tab.id}`}
                    className={`group flex items-center gap-3 rounded-lg px-3 py-2 text-xs font-semibold transition-all duration-200 ${
                      isLocked
                        ? 'opacity-50 cursor-not-allowed text-slate-400 hover:bg-transparent hover:text-slate-400'
                        : isActive
                        ? 'bg-blue-50 text-blue-700'
                        : 'text-slate-600 hover:bg-slate-50 hover:text-blue-600'
                    }`}
                    onClick={(e) => {
                      if (isLocked) {
                        e.preventDefault();
                      } else {
                        onClose();
                      }
                    }}
                    title={isLocked ? "Selesaikan & setujui Perencanaan (RAB & Jadwal) terlebih dahulu agar proyek aktif" : undefined}
                  >
                    <span className={isLocked ? 'text-slate-400' : isActive ? 'text-blue-600' : 'text-slate-400 group-hover:text-blue-500'}>
                      {isLocked ? <Lock size={16} /> : tab.icon}
                    </span>
                    {tab.label}
                  </NavLink>
                );
              })}
            </div>
          </div>
        )}

      </nav>

      <div className="space-y-4 px-5 pb-6">
        <div className="relative min-h-[190px] overflow-hidden rounded-2xl bg-slate-50 p-4 ring-1 ring-slate-200">
          <div className="relative z-10 max-w-[142px]">
            <h4 className="text-sm font-extrabold text-slate-800">Butuh Bantuan?</h4>
            <p className="mt-2 text-xs leading-5 text-slate-500">
              Tim Indoteknisi siap membantu operasional dan proyek Anda.
            </p>
            <a
              href="tel:02150992929"
              className="mt-4 inline-flex items-center gap-2 rounded-lg bg-white px-3 py-2 text-xs font-extrabold text-blue-700 ring-1 ring-slate-200 transition hover:bg-blue-50"
            >
              <Phone size={15} />
              Hubungi Kami
            </a>
          </div>
          <img
            src={indoteknisiMan}
            alt="Tim support Indoteknisi"
            className="absolute -bottom-2 -right-5 h-[150px] w-auto object-contain drop-shadow-sm"
          />
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
